import { Badge, Box, Card, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { VideonystagmographyTestResult } from "types/testTypes";
import { useEffect, useMemo, useState } from "react";
import GazeDisplacementViewer from "@components/common/GazeDisplacementViewer";
import PupilSizeChart from "@components/common/PupilSizeChart";
import { NystagmusSummary, computeNystagmusSummary } from "@utils/nystagmusSummary";

export default function VideonystagmographyTestEntry({ entry }) {
  const testResult = entry as VideonystagmographyTestResult;

  const [gazeDatas, setGazeDatas] = useState(testResult?.results.gazeDatas || null);
  const [pupilSizeData, setPupilSizeData] = useState(testResult?.results?.pupilSizeDatas || null);

  const summary = useMemo(() => computeNystagmusSummary(gazeDatas), [gazeDatas]);

  useEffect(() => {
    setGazeDatas(testResult?.results.gazeDatas || null);
    setPupilSizeData(testResult?.results?.pupilSizeDatas || null);
  }, [testResult]);
  return (
    <Box w="100%" h="100%" p="md">
      <Stack gap="md">
        <NystagmusSummaryCard summary={summary} hasData={Boolean(gazeDatas?.length)} />
        {pupilSizeData && <PupilSizeChart data={pupilSizeData} />}
        {gazeDatas && <GazeDisplacementViewer data={gazeDatas} />}
      </Stack>
    </Box>
  );
}

interface NystagmusSummaryCardProps {
  summary: NystagmusSummary | null;
  hasData: boolean;
}

function formatNumber(value: number | null, suffix = "") {
  if (value === null || Number.isNaN(value)) return "—";
  return `${value}${suffix}`;
}

function formatPercent(value: number | null) {
  if (value === null || Number.isNaN(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

function formatLabel(value: string) {
  if (!value || value === "indeterminate") return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatWindowRange(
  startSeconds: number | null,
  endSeconds: number | null,
  durationSeconds: number | null,
) {
  if (startSeconds === null || endSeconds === null) {
    return formatNumber(durationSeconds ?? null, "s");
  }
  return `${formatNumber(startSeconds, "s")} → ${formatNumber(endSeconds, "s")} (${formatNumber(durationSeconds ?? null, "s")})`;
}

function NystagmusSummaryCard({ summary, hasData }: NystagmusSummaryCardProps) {
  const status = (() => {
    if (!hasData) return { color: "gray", label: "No gaze data" };
    if (!summary) return { color: "gray", label: "Analyzing" };
    if (!summary.hasEnoughData) return { color: "yellow", label: "Not enough data" };
    return summary.detected
      ? { color: "red", label: "Nystagmus detected" }
      : { color: "green", label: "Not detected" };
  })();

  const CRITERIA_MESSAGES: Record<keyof NystagmusSummary["detectionCriteria"], string> = {
    hasEnoughData: "Collect more data for accurate analysis.",
    amplitudeAboveThreshold: "Eye movement range is below detection threshold.",
    amplitudeBeatsNoise: "Eye movement signal needs to be clearer.",
    minimumCyclesMet: "Need more complete eye movement cycles.",
    frequencyWithinPhysiologicalRange: "Movement frequency outside expected range (0.5-7 Hz).",
    slowPhaseVelocityPresent: "Slow-phase eye movement pattern not yet clear.",
  };

  const helperText = (() => {
    if (!hasData) return "Eye tracking data needed to analyze.";
    if (!summary) return "Analyzing eye movements…";
    if (!summary.hasEnoughData) return "Gather more data for reliable results.";
    if (summary && !summary.detected) {
      const failingCriterion = Object.entries(summary.detectionCriteria).find(
        ([, value]) => !value,
      );
      if (failingCriterion) {
        return CRITERIA_MESSAGES[failingCriterion[0] as keyof typeof CRITERIA_MESSAGES];
      }
    }
    return null;
  })();

  const metrics = [
    { label: "Primary axis", value: formatLabel(summary?.dominantAxis ?? "") },
    { label: "Direction", value: formatLabel(summary?.direction ?? "") },
    { label: "Movement size", value: formatNumber(summary?.amplitude ?? null, "°") },
    { label: "Frequency", value: formatNumber(summary?.frequencyHz ?? null, " Hz") },
    {
      label: "Movement speed",
      value: formatNumber(summary?.slowPhaseVelocity ?? null, "°/s"),
    },
    { label: "Risk score", value: formatPercent(summary?.detectionRiskScore ?? null) },
  ];

  if (summary?.detected) {
    metrics.splice(5, 0, {
      label: "Window",
      value: formatWindowRange(
        summary.windowStartSeconds,
        summary.windowEndSeconds,
        summary.durationSeconds,
      ),
    });
  }

  return (
    <Card withBorder radius="md" shadow="xs" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Nystagmus Summary</Text>
            <Text size="sm" c="dimmed" mb="xs">
              Tracks involuntary eye movements to assess inner ear balance and spatial orientation.
            </Text>
            <Text size="sm" c="dimmed">
              {summary
                ? `${summary.sampleCount} samples · ${summary.windowsEvaluated} windows`
                : hasData
                  ? "Analyzing"
                  : "—"}
            </Text>
          </div>
          <Badge variant="light" color={status.color} size="lg">
            {status.label}
          </Badge>
        </Group>
        {helperText && (
          <Text size="sm" c="dimmed">
            {helperText}
          </Text>
        )}
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
          {metrics.map((metric) => (
            <Stack key={metric.label} gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {metric.label}
              </Text>
              <Text fw={500}>{metric.value}</Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
}
